import { motion, useScroll, useAnimate, useInView } from 'framer-motion';
import { useRef, useState, useEffect, useCallback } from 'react';
import { MotionImage, useSanitizedTranslation, StackedContent, XumoButton } from 'ui';
import { Modal } from './Modal';

import Image from 'next/image';

export interface CarouselCTA {
  href: string;
  color?: string;
}

export type ToggleModalFn = (index?: number) => void;

export interface BannerCarouselItemProps {
  img: { src: string; alt: string };
  logo: { src: string; alt: string };
  oldPrice: { src: string; alt: string };
  newPrice: { src: string; alt: string };
  copy: string;
  button: { label: string; href: string };
  pricingInfo: string;
  index: number;
  toggleModal: ToggleModalFn;
}

export interface CarouselProps {
  bg?: string;
  sectionClass?: string;
  items: BannerCarouselItemProps[];
}

const carouselItems = [
  {
    img: {
      src: '/static/images/refresh/2025/black-friday-banner/hod.jpg',
      alt: 'House of the Dragon',
    },
    logo: {
      src: '/static/images/refresh/2025/logos/hbo-max.svg',
      alt: 'HBO Max',
    },
    oldPrice: {
      src: '/static/images/refresh/2025/black-friday-banner/prices/10.99.svg',
      alt: 'Was $10.99',
    },
    newPrice: {
      src: '/static/images/refresh/2025/black-friday-banner/prices/2.99.svg',
      alt: 'Now $2.99',
    },
    copy: 'Basic with Ads plan',
    button: { label: 'Shop now', href: 'https://www.xumo.com/subscriptions?intcmp=ILC:XMP:bufl:0:INB:blackfriday:blackfriday_max:910087' },
    pricingInfo: 'Valid on Basic With Ads plan only. Offer availability varies by subscription provider. Ends 12/1/25. See HBOMax.com for terms.',
  },
  {
    img: {
      src: '/static/images/refresh/2025/black-friday-banner/homealone.jpg',
      alt: 'Home Alone',
    },
    logo: {
      src: '/static/images/refresh/2025/black-friday-banner/logos/disney-hulu.svg',
      alt: 'Disney+ and Hulu',
    },
    oldPrice: {
      src: '/static/images/refresh/2025/black-friday-banner/prices/12.99.svg',
      alt: 'Was $12.99',
    },
    newPrice: {
      src: '/static/images/refresh/2025/black-friday-banner/prices/4.99.svg',
      alt: 'Now $4.99',
    },
    copy: 'Ad-supported Disney+, Hulu Bundle',
    button: { label: 'Learn more', href: 'https://www.xumo.com/blackfriday2025?intcmp=ILC:XMP:mktng:0:INB:blackfriday:blackfriday_disney:910090' },
    pricingInfo:
      'Ends 12/1/25. Offer for ad-supported Disney+, Hulu Bundle only. $4.99/mo for 12 mos, then $12.99/mo (or then current regular monthly price). U.S. residents, 18+ only. Eligibility restrictions and terms apply. &copy;2025 Disney and its related entities.',
  },
  {
    img: {
      src: '/static/images/refresh/2025/black-friday-banner/pluribus.jpg',
      alt: 'Pluribus',
    },
    logo: {
      src: '/static/images/refresh/2025/black-friday-banner/logos/apple-tv.svg',
      alt: 'Apple TV+',
    },
    oldPrice: {
      src: '/static/images/refresh/2025/black-friday-banner/prices/12.99.svg',
      alt: 'Was $12.99',
    },
    newPrice: {
      src: '/static/images/refresh/2025/black-friday-banner/prices/5.99.svg',
      alt: 'Now $5.99',
    },
    copy: 'Monthly subscription',
    button: { label: 'Learn more', href: 'https://www.xumo.com/blackfriday2025?intcmp=ILC:XMP:mktng:0:INB:blackfriday:blackfriday_apple:910091' },
    pricingInfo: 'Apple TV subscription offer for eligible users and devices only. $12.99/mo after offer period. Terms apply.',
  },
  {
    img: {
      src: '/static/images/refresh/2025/black-friday-banner/liga.jpg',
      alt: 'Liga BBVA',
    },
    logo: {
      src: '/static/images/refresh/2025/black-friday-banner/logos/vix-premium.svg',
      alt: 'ViX Premium',
    },
    oldPrice: {
      src: '/static/images/refresh/2025/black-friday-banner/prices/59.99.svg',
      alt: 'Was $59.99',
    },
    newPrice: {
      src: '/static/images/refresh/2025/black-friday-banner/prices/49.99.svg',
      alt: 'Now $49.99',
    },
    copy: 'Vix Premium No Ads annual subscription',
    button: { label: 'Shop now', href: 'https://www.xumo.com/subscriptions?intcmp=ILC:XMP:bufl:0:INB:blackfriday:blackfriday_vix:910088' },
    pricingInfo:
      'Offer from 11/24/25 to 12/1/25. Black outs and restrictions apply. Limited to annual plan. New ViX Premium Sin Anuncios subscribers only. After promo, regular rates apply, First year $49.99 (plus applicable taxes), then autorenewal at the then current ViX Premium Sin Anuncios annual subscription fee (currently $59.99/year plus applicable taxes). Subscriptions auto-renews until canceled. App contains Spanish language content only. Requires a Xumo and ViX Account.',
  },
  {
    img: {
      src: '/static/images/refresh/2025/black-friday-banner/force.jpg',
      alt: 'Power Book IV: Force',
    },
    logo: {
      src: '/static/images/refresh/2025/black-friday-banner/logos/starz.svg',
      alt: 'STARZ',
    },
    oldPrice: {
      src: '/static/images/refresh/2025/black-friday-banner/prices/10.99.svg',
      alt: 'Was $10.99',
    },
    newPrice: {
      src: '/static/images/refresh/2025/black-friday-banner/prices/2.99-2.svg',
      alt: 'Now $2.99',
    },
    copy: 'Monthly subscription',
    button: { label: 'Shop now', href: 'https://www.xumo.com/subscriptions?intcmp=ILC:XMP:bufl:0:INB:blackfriday:blackfriday_starz:910089' },
    pricingInfo:
      'Ends 12/1/25. Offer available ONLY to new and returning STARZ subscribers who subscribe through Xumo. Offer does not include free trial. After completion of three (3) month promotional offer, service automatically rolls month-to-month at the then current price (currently $10.99/month plus applicable taxes) unless cancelled. Cancellation effective at end of then-current billing period. Subscription is non-refundable. Offer is limited to one per customer and account. Offer may not be combined with other offers. Standard rates subject to change. Starz® and related channels and service marks are the property of Starz Entertainment, LLC.',
  },
];

const BannerCarouselItem = ({ img, logo, oldPrice, newPrice, copy, button, toggleModal, index }: BannerCarouselItemProps) => {
  const { t } = useSanitizedTranslation();

  return (
    <div className="flex snap-x flex-col gap-10 lg:gap-12">
      <div
        className="relative z-20 mt-8 flex h-full w-max min-w-[365px] max-w-[365px] snap-center flex-col rounded-xl sm:min-w-[415px] sm:max-w-[415px]"
        style={{ boxShadow: '0 0 20px rgba(0, 0, 0, 0.35)' }}
      >
        {/* Image */}
        <div className="overflow-clip rounded-t-xl">
          <Image src={img.src} alt={img.alt} width={416} height={234} />
        </div>

        {/* Content */}
        <div className="-mt-[1px] flex h-full min-h-[300px] w-full flex-col justify-between gap-8 rounded-b-xl bg-[#190A19] p-6 text-white">
          {/* Logo */}
          <div className="flex justify-between">
            <Image src="/static/images/refresh/2025/icons/deals.svg" alt="Deals icon" width={90} height={50} />
            <div className="relative flex h-[60px] w-full max-w-[190px] justify-end lg:max-w-[210px]">
              <Image src={logo?.src} alt={logo?.alt + ' logo'} fill className="object-contain object-right" />
            </div>
          </div>

          {/* Prices */}
          <div className="flex justify-between">
            <Image src={oldPrice?.src} alt={oldPrice?.alt} width={127} height={31} />
            <Image src={newPrice?.src} alt={newPrice?.alt} width={180} height={61} />
          </div>

          {/* Copy */}
          <div className="type-card-body min-h-[60px]" data-i18n dangerouslySetInnerHTML={{ __html: t(copy) }} />
        </div>
      </div>
      <XumoButton href={button?.href} label={button?.label} target="_blank" />
      <button onClick={() => toggleModal(index)} className="text-[14px] underline">
        Pricing and other info
      </button>
    </div>
  );
};

const BannerCarouselContainer = ({ items, scrollRef, toggleModal }: any) => {
  const { t } = useSanitizedTranslation();

  const internalScrollerRef = useRef<HTMLDivElement | null>(null);
  const scrollerRef = scrollRef || internalScrollerRef;
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();

  const [scrollProgress, setScrollProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const scrollbarTrackRef = useRef<HTMLButtonElement | null>(null);

  // Use Framer Motion's scroll tracking for horizontal scroll
  const { scrollXProgress } = useScroll({
    container: scrollerRef,
  });

  // Update progress state when scrollXProgress changes
  useEffect(() => {
    return scrollXProgress.on('change', latest => {
      setScrollProgress(latest);
    });
  }, [scrollXProgress]);

  // --- Scroll to specific card when indicator clicked
  const scrollToIndex = useCallback((index: number) => {
    const el = scrollerRef.current;
    const card = cardRefs.current[index];
    if (!el || !card) return;

    setIsUserScrolling(true);

    const cardLeft = card.offsetLeft;
    const cardWidth = card.offsetWidth;
    const containerWidth = el.clientWidth;
    const targetScroll = cardLeft - (containerWidth - cardWidth) / 2;

    el.scrollTo({
      left: Math.max(0, targetScroll),
      behavior: 'smooth',
    });

    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => setIsUserScrolling(false), 600);
  }, []);

  const handleSelect = (index: number) => {
    scrollToIndex(index);
  };

  // --- Handle scrollbar interaction (click and drag)
  const handleScrollbarInteraction = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const track = scrollbarTrackRef.current;
    const scroller = scrollerRef.current;
    if (!track || !scroller) return;

    const rect = track.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const trackWidth = rect.width;
    const indicatorWidth = 150;

    // Calculate progress (0 to 1) accounting for indicator width
    const maxTravel = trackWidth - indicatorWidth;
    const targetProgress = Math.max(0, Math.min(1, clickX / maxTravel));

    // Calculate target scroll position
    const maxScroll = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
    const targetScroll = targetProgress * maxScroll;

    scroller.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      setIsDragging(true);
      handleScrollbarInteraction(e);
    },
    [handleScrollbarInteraction],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const track = scrollbarTrackRef.current;
      const scroller = scrollerRef.current;
      if (!track || !scroller) return;

      const rect = track.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const trackWidth = rect.width;
      const indicatorWidth = 150;

      const maxTravel = trackWidth - indicatorWidth;
      const targetProgress = Math.max(0, Math.min(1, clickX / maxTravel));

      const maxScroll = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
      const targetScroll = targetProgress * maxScroll;

      scroller.scrollTo({
        left: targetScroll,
        behavior: 'auto', // Use 'auto' for smooth dragging
      });
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add/remove mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // --- Track scroll position and update current card index
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    let rafId = 0;

    const updateCurrentIndex = () => {
      const el = scrollerRef.current;
      if (!el) return;

      const scrollLeft = el.scrollLeft;
      const containerWidth = el.clientWidth;
      const scrollCenter = scrollLeft + containerWidth / 2;

      let closest = 0;
      let min = Infinity;

      cardRefs.current.forEach((card, i) => {
        if (!card) return;
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const d = Math.abs(scrollCenter - cardCenter);
        if (d < min) {
          min = d;
          closest = i;
        }
      });

      if (closest !== currentIndex) setCurrentIndex(closest);
    };

    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateCurrentIndex);
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    updateCurrentIndex();

    return () => {
      el.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [currentIndex, isUserScrolling]);

  return (
    <div className="-mt-[230px] lg:-mt-[298px] xl:-mt-[270px] 2xl:-mt-[240px]">
      {/* Scrollable cards */}
      <div
        ref={scrollerRef}
        className="hide-scroll flex w-full snap-x snap-mandatory snap-center items-start gap-10 overflow-x-auto pb-8 pr-8 md:snap-none lg:gap-8 lg:pb-16 lg:pr-0"
      >
        <div aria-hidden className="w-[8px] shrink-0 md:w-[32px] lg:w-[calc((100vw-1034px)/2)] xl:w-[calc((100vw-1214px)/2)] 2xl:w-[calc((100vw-1470px)/2)]" />
        {items.map((item: any, index: number) => (
          <div
            key={index}
            ref={el => {
              cardRefs.current[index] = el;
            }}
            className="shrink-0 snap-center"
            style={{
              scrollSnapAlign: 'center',
              scrollSnapStop: 'always',
            }}
          >
            <BannerCarouselItem {...item} toggleModal={toggleModal} index={index} />
          </div>
        ))}
        <div aria-hidden className="w-[8px] shrink-0 md:w-[32px] lg:w-[calc((100vw-1034px)/2)] xl:w-[calc((100vw-1214px)/2)] 2xl:w-[calc((100vw-1470px)/2)]" />
      </div>

      {/* Mobile scroll indicators */}
      <div className="z-20 flex w-full justify-center gap-2 pb-16 md:hidden">
        {items.map((_, index) => (
          <button
            key={index}
            onClick={() => handleSelect(index)}
            className={`h-3 w-3 rounded-full transition-all duration-300 ${currentIndex === index ? 'w-8 bg-xumoDarkPlum' : 'bg-xumoDarkPlum/30'}`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Desktop scrollbar */}
      <div className="hidden w-full justify-center pb-16 md:flex">
        <button
          ref={scrollbarTrackRef}
          onMouseDown={handleMouseDown}
          className="relative z-20 flex h-[10px] w-[750px] cursor-pointer items-center overflow-hidden rounded-full border-0 bg-slate-200 p-0"
          aria-label="Scroll carousel"
        >
          <div
            className="pointer-events-none h-[10px] w-[150px] rounded-full bg-[#190a19]"
            style={{
              transform: `translateX(${scrollProgress * 600}px)`,
              transition: isDragging ? 'none' : 'transform 200ms ease-out',
            }}
          ></div>
        </button>
      </div>
    </div>
  );
};

export interface BannerCarouselProps {
  bgImg: string;
  bgImgMobile: string;
  showScrollButtons: boolean;
}

export const BannerCarousel = ({ bgImg, bgImgMobile, showScrollButtons = false }: BannerCarouselProps) => {
  const { t } = useSanitizedTranslation();

  const [ref, animate] = useAnimate();
  const inView = useInView(ref, { once: true, margin: '-30% 0% -30% 0%' });
  const carouselScrollRef = useRef<HTMLDivElement | null>(null);

  const [showModal, setShowModal] = useState(false);
  const itemNumber = useRef(0);

  // keep buttons visible; only disable when at edges
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateArrows = () => {
    const el = carouselScrollRef.current;
    if (!el) return;
    const maxScroll = Math.max(el.scrollWidth - el.clientWidth, 0);
    const current = el.scrollLeft;
    const EPS = 2; // tiny buffer for fractional scroll values

    setCanScrollLeft(current > EPS);
    setCanScrollRight(current < maxScroll - EPS);
  };

  const scrollCarousel = (direction: 'left' | 'right') => {
    const el = carouselScrollRef.current;
    if (!el) return;

    // use responsive step so it feels right at all widths
    const step = Math.max(Math.floor(el.clientWidth * 0.8), 300);
    const maxScroll = Math.max(el.scrollWidth - el.clientWidth, 0);

    const target = direction === 'left' ? Math.max(0, el.scrollLeft - step) : Math.min(maxScroll, el.scrollLeft + step);

    el.scrollTo({ left: target, behavior: 'smooth' });
  };

  useEffect(() => {
    const el = carouselScrollRef.current;
    if (!el) return;

    // Update on scroll
    el.addEventListener('scroll', updateArrows, { passive: true });

    // Update on resize
    const onResize = () => updateArrows();
    window.addEventListener('resize', onResize);

    // Update when content size changes
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);

    // Initial check
    updateArrows();

    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', onResize);
      ro.disconnect();
    };
  }, []);

  const toggleModal = useCallback((index?: number) => {
    typeof index === 'number' ? (itemNumber.current = index) : 0;
    setShowModal(prev => !prev);
  }, []);

  return (
    <motion.section ref={ref} className="relative bg-white pt-8 text-xumoDarkPlum lg:pt-16" data-testid={'text-section'}>
      <div className="wrapper">
        <div className="relative h-[600px] overflow-clip rounded-2xl text-white lg:h-[607px]">
          <Image src={bgImg} alt="" fill className="hidden object-cover lg:block" />
          <Image src={bgImgMobile} alt="" fill className="block object-cover lg:hidden" />
          <div className="content-wrapper absolute inset-0 m-2 flex flex-col lg:m-0 lg:flex-row">
            <div className="w-full lg:w-1/2">
              <StackedContent
                fullalways
                fullheight
                content={[
                  { image: { src: '/static/images/refresh/2025/logos/xumo-white.svg', alt: 'Xumo', width: 170, height: 141, rederedWidth: 170 } },
                  { heading: '<strong>Black Friday</strong><br/>Deals' },
                ]}
              />
            </div>

            <motion.div
              ref={ref}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: '-30% 0% -30% 0%' }}
              className="flex w-full flex-col gap-6 lg:w-1/2 lg:gap-12"
            >
              <p
                className="type-subheading-light"
                data-i18n
                dangerouslySetInnerHTML={{ __html: t('<strong>Save up to 70% off top apps</strong> — watch more, save more. Deals end 12/1.') }}
              />
              <div className="flex items-start gap-2">
                <Image src={'/static/images/refresh/2025/icons/microphone.svg'} alt="Remote microphone icon" width={40} height={40} />
                <p
                  className="type-body"
                  data-i18n
                  dangerouslySetInnerHTML={{ __html: t('Say "Black Friday deals" into your<br/>voice remote to shop more.') }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="relative w-screen">
        <BannerCarouselContainer items={carouselItems} scrollRef={carouselScrollRef} toggleModal={toggleModal} />
      </div>

      <Modal
        toggleModal={toggleModal}
        showModal={showModal}
        headingBold="Pricing and other info"
        disclaimer="Black Friday offers will be promoted and sold on Xumo platforms from November 24, 2025 at 9am through December 2, 2025 at 3am EST."
        body={carouselItems[itemNumber.current].pricingInfo}
      />

      {/* Chevron scroll buttons (always visible; disabled at edges) */}
      {showScrollButtons && (
        <div className="absolute bottom-[30.5%] flex w-full justify-between">
          {/* Left */}
          <button
            type="button"
            onClick={() => scrollCarousel('left')}
            disabled={!canScrollLeft}
            aria-disabled={!canScrollLeft}
            className="z-20 flex h-16 w-10 items-center justify-center rounded-r-xl border-[1px] border-xumoDarkPlum bg-xumoDarkPlum text-white transition-all duration-300 hover:border-xumoDarkPlum disabled:border-[#EFF3F5] disabled:opacity-40 md:w-20 md:p-4"
            aria-label="Scroll left"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} className="h-8 w-8 stroke-current">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
          </button>
          {/* Right */}
          <button
            type="button"
            onClick={() => scrollCarousel('right')}
            disabled={!canScrollRight}
            aria-disabled={!canScrollRight}
            className="z-20 flex h-16 w-10 items-center justify-center rounded-l-xl border-[1px] border-xumoDarkPlum bg-xumoDarkPlum text-white transition-all duration-300 hover:border-xumoDarkPlum disabled:border-[#EFF3F5] disabled:opacity-40 md:w-20 md:p-4"
            aria-label="Scroll right"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} className="h-8 w-8 stroke-current">
              <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>
      )}
    </motion.section>
  );
};
